import { AppContext } from "01Data/AppData";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  TextField,
  Typography,
  styled,
} from "@mui/material";
import { CognitoUser } from "amazon-cognito-identity-js";
import QRCode from "qrcode.react";
import { FC, useContext, useState } from "react";
import { Account } from "../01Data/Account";

type DialogResult = "ok" | "cancel";

type DialogProps = {
  open: boolean;
  onClose: (result: DialogResult) => void;
  account: Account;
  user: CognitoUser;
  secret: string;
  email: string;
};

const ErrorText = styled(Typography)(() => ({
  fontSize: "0.8rem",
  color: "#f00",
  whiteSpace: "pre-line",
}));

const QRDialogContent: FC<DialogProps> = ({
  open,
  onClose,
  account,
  user,
  secret,
}) => {
  const [totpCode, setTotpCode] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const email = user.getSignInUserSession()?.getIdToken().payload.email;

  const handleApply = async () => {
    try {
      // console.log(user);
      const output: any = await account.verifyTotpToken(user, totpCode);
      if (output.Status === "SUCCESS") {
        await account.setPrefferedMfa("TOTP");
        onClose("ok");
      }
    } catch (error) {
      console.log(error);
      setErrorMessage(
        `コードが正しくありません。もう一度試してください。\n` +
          `${(error as Error).toString()}`
      );
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(secret);
  };

  return (
    <Dialog
      open={open}
      onClose={() => onClose("cancel")}
      aria-labelledby="qr-dialog-title"
    >
      <DialogTitle id="qr-dialog-title">MFA デバイスの設定</DialogTitle>
      <DialogContent>
        <List dense={true} sx={{ listStyle: "decimal" }}>
          <ListItem sx={{ display: "list-item" }}>
            <ListItemText primary="Google Authenticator、Duo Mobile、Authy アプリなどの互換性のあるアプリケーションを、モバイルデバイスまたはコンピュータにインストールします。" />
          </ListItem>
          <ListItem sx={{ display: "list-item" }}>
            <ListItemText primary="認証アプリを開いて、コードをスキャンします。または、シークレットキーを入力することもできます。" />
            <QRCode
              value={`otpauth://totp/RousuiMihariban:${email}?secret=${secret}&issuer=KyokkoIns`}
            />
            <Box display="flex" alignItems="center" gap={2}>
              <TextField
                sx={{ width: "60%" }}
                variant="outlined"
                margin="dense"
                value={secret}
                InputProps={{ readOnly: true }}
              />
              <Button
                variant="contained"
                size="small"
                sx={{ height: "42px" }}
                onClick={handleCopy}
              >
                コピー
              </Button>
            </Box>
          </ListItem>
          <ListItem sx={{ display: "list-item" }}>
            <ListItemText primary="MFA デバイスに表示されたコードを入力して、確認ボタンを押してください。" />
          </ListItem>
        </List>
        <TextField
          autoComplete="off"
          required
          inputProps={{ inputMode: "numeric" }}
          sx={{ width: "60%" }}
          variant="filled"
          margin="normal"
          label="MFA コード"
          onChange={(e) => {
            setTotpCode(e.target.value);
          }}
        />
        {errorMessage ? <ErrorText>{errorMessage}</ErrorText> : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleApply}>確認</Button>
        <Button onClick={() => onClose("cancel")}>ユーザー設定に戻る</Button>
      </DialogActions>
    </Dialog>
  );
};

export const useQRDialog = () => {
  const a = useContext(AppContext);
  const [open, setOpen] = useState(false);
  const [resolve, setResolve] = useState<(value: DialogResult) => void>();

  const openQRDialog = () => {
    setOpen(true);
    return new Promise<DialogResult>((resolve) => {
      setResolve(() => resolve);
    });
  };
  const onClose = (result: DialogResult) => {
    setOpen(false);
    if (resolve) {
      resolve(result);
    }
  };

  const QRDialog: FC<{ user: CognitoUser; secret: string; email: string }> = (
    props
  ) => (
    <QRDialogContent
      open={open}
      onClose={onClose}
      account={a.account}
      user={props.user}
      secret={props.secret}
      email={props.email}
    />
  );

  return {
    QRDialog,
    openQRDialog,
  };
};
